"use client";

import { createContext, useContext, useEffect, useState } from "react";

import type { PostHog } from "posthog-js";
import type { default as React } from "react";
import { runWhenIdle } from "../../utils";

export interface PostHogProps extends PostHogPageViewProps {
	apiKey?: string;
	apiHost?: string;
	personProfiles?: "identified_only" | "always" | "never";
	disableSurveys?: boolean;
}

interface PostHogPageViewProps {
	pathname?: string;
	searchParams?: URLSearchParams;
}

const PostHogContext = createContext<PostHog | null>(null);

// Local usePostHog — returns null until posthog-js resolves from its dynamic import.
// Consumers must tolerate null (capture/identify/etc. are no-ops when the SDK is not loaded).
// We don't use posthog-js/react's hook because that package statically imports posthog-js,
// which would pull the full SDK into the first-load bundle.
export function usePostHog(): PostHog | null {
	return useContext(PostHogContext);
}

export function PostHogProvider({
	apiKey,
	apiHost,
	personProfiles,
	disableSurveys = false,
	pathname,
	searchParams,
	children,
}: React.PropsWithChildren<PostHogProps>) {
	const [posthogClient, setPosthogClient] = useState<PostHog | null>(null);

	useEffect(() => {
		if (!apiKey) {
			return;
		}

		let mounted = true;

		const init = async () => {
			try {
				const { default: posthog } = await import("posthog-js");
				posthog.init(apiKey, {
					api_host: apiHost || "https://us.i.posthog.com",
					person_profiles: personProfiles,
					capture_pageview: false,
					autocapture: false,
					advanced_disable_feature_flags: true,
					disable_surveys: disableSurveys,
				});
				if (mounted) {
					setPosthogClient(posthog);
				}
			} catch {
				// Ignore init failures (ad blockers, network errors, blocked scripts)
			}
		};

		const cancel = runWhenIdle(() => {
			void init();
		});

		return () => {
			mounted = false;
			cancel();
		};
	}, [apiKey, apiHost, personProfiles, disableSurveys]);

	// Context.Provider identity is stable across renders, so children stay mounted
	// while posthog-js loads in the background. Only the context value flips when
	// the client arrives — no subtree unmount/remount.
	return (
		<PostHogContext.Provider value={posthogClient}>
			{posthogClient && <PostHogPageView pathname={pathname} searchParams={searchParams} />}
			{children}
		</PostHogContext.Provider>
	);
}

function PostHogPageView({ pathname, searchParams }: { pathname?: string; searchParams?: URLSearchParams }) {
	const posthog = usePostHog();

	useEffect(
		function trackPageView() {
			if (pathname && posthog) {
				let url = window.location.origin + pathname;
				if (searchParams instanceof URLSearchParams && searchParams.toString()) {
					url += `?${searchParams.toString()}`;
				}

				posthog.capture("$pageview", { $current_url: url });
			}
		},
		[pathname, searchParams, posthog],
	);

	return null;
}
